<template lang="pug">
  div(
    ref="rootRef"
    :style="itemsToRender.length < $get(items, `length`) ? `height: ${scrolled + 10000}px` : undefined"
    style="width: 100%")
    div(
      ref="containerRef"
      style="width: 100%")
      utility-compute-height(
        v-for="({ item, height }, index) in itemsToRender"
        :key="index"
        :is-activated="topHiddenItemsCount <= index && index <= itemsToRender.length - bottomHiddenItemsCount - 1"
        @input="updateHeight({ index, value: $event })")
        slot(
          v-if="topHiddenItemsCount <= index && index <= itemsToRender.length - bottomHiddenItemsCount - 1"
          :item="item")
        div(
          v-else
          :style="`height: ${height}px`")
</template>

<script>
import { onUpdated, ref, onMounted, watch, computed, onBeforeUnmount } from '@vue/composition-api'
import useWindow from '@iez/lib/use-window/'
import { vuetify } from '@iez/core'
import UtilityComputeHeight from './utility-compute-height/'
import { scrollBehaviorResult } from '@iez/core/router'
import waitFor from '@iez/lib/wait-for/'
import { routeCurrent } from '@iez/core/router'

const cache = {}

export default {
  components: {
    UtilityComputeHeight
  },

  props: {
    items: Array
  },
  setup (props, { slots }) {
    const { fullPath } = routeCurrent
    const rootRef = ref()
    const containerRef = ref()
    const itemsToRender = ref([])
    const window = useWindow()
    const containerBottom = ref(Number.NEGATIVE_INFINITY)
    const containerTop = ref(Number.POSITIVE_INFINITY)
    const offset = 2000
    const windowBottom = computed(
      () => window.height + offset - vuetify.framework.application.bottom
    )
    const windowTop = computed(
      () => vuetify.framework.application.top - offset
    )
    const scrolled = computed(
      () => windowTop.value - containerTop.value
    )
    const remainingToScroll = computed(
      () => containerBottom.value - windowBottom.value
    )
    const shouldAddNextToRender = computed(
      () => remainingToScroll.value < 0
    )
    const topHiddenItemsCount = computed(
      () => {
        const reduce = itemsToRender.value
          .map(({ height }) => height)
          .reduce(
            (accumulator, height, index) =>
              typeof accumulator === `object`
                ? accumulator.scrolled - height < 0
                  ? accumulator.result
                  : {
                  scrolled: accumulator.scrolled - height,
                  result: accumulator.result + 1
                } : accumulator
            ,
            { scrolled: scrolled.value, result: 0 }
          )
        return typeof reduce === `object`
          ? reduce.result
          : reduce
      }
    )
    const bottomHiddenItemsCount = computed(
      () => {
        const reduce = itemsToRender.value
          .reduce(
            (accumulator, _, index) => {
              const indexReversed = itemsToRender.value.length - index - 1
              const { height } = itemsToRender.value[indexReversed]
              return typeof accumulator === `object`
                ? accumulator.remainingToScroll - height < 0
                  ? accumulator.result
                  : {
                  remainingToScroll: accumulator.remainingToScroll - height,
                  result: accumulator.result + 1
                } : accumulator
            }
            ,
            { remainingToScroll: remainingToScroll.value, result: 0 }
          )
        return typeof reduce === `object`
          ? reduce.result
          : reduce
      }
    )
    const addNextToRender = () => {
      // only add to render if has remaining
      const itemToRender = props.items?.[
        itemsToRender.value.length
      ]
      if (itemToRender) {
        itemsToRender.value.push({
          item: itemToRender,
          height: 0
        })
      } else {
        // we have finished !
        rootRef.value.style.height = null
        // unset first height call with average item height
      }
    }

    const tryAddNextToRender = () => {
      computedContainerBounds()
      if (shouldAddNextToRender.value) {
        addNextToRender()
      }
    }

    const updateHeight = ({ index, value }) => {
      itemsToRender.value[index].height = value
    }


    /**
     * On first render and when items is fetched
     * push first item to render
     */
    watch(
      () => props.items,
      items => {
        /**
         * update / remove rendered items if props.items changes
         */
        for (let index = itemsToRender.value.length - 1 ; index !== -1 ; index--) {
          const itemToRender = itemsToRender.value[index]
          const matchedItem = items.find(({ id }) => itemToRender.item.id === id)
          if (matchedItem) {
            /**
             * if itemToRender is in items, update it
             */
            itemToRender.item = matchedItem
          } else {
            /**
             * else remove it
             */
            itemsToRender.value.splice(
              index,
              1
            )
          }
        }

        if (
          // we ensure its first render (itemsToRender empty)
          !itemsToRender.value.length &&
          props.items?.length
          // we also ensure items is fetched (items.length)
        ) {
          // pushing first item to render
          addNextToRender()
        }
      }, {
        immediate: true
      }
    )

    /**
     * On DOM update (after render hopefully)
     * We check if there is empty space to render
     * another item (if has remaining)
     */
    const computedContainerBounds = () => {
      const { bottom, top } = containerRef.value.getBoundingClientRect()
      containerBottom.value = bottom
      containerTop.value = top
    }
    onUpdated(
      () => {
        tryAddNextToRender()
      }
    )

    /**
     * We want to simulate virtual scroll
     * So when user scroll, we dynamically
     * append item (if there are
     * remaining to append)
     * */
    /**
     * On scroll
     * If there is a small _offset_ before
     * all content is scrolled,
     * we append an item (to make
     * available to scrol again)
     */
    onMounted(
      async () => {
        const { y: scrollBehaviorResultY } = await waitFor({
          dependencies: () => scrollBehaviorResult.value,
          predicate: scrollBehaviorResult => !!scrollBehaviorResult
        })
        /**
         * Fetch from cache (for scroll to behavior)
         */
        /* if (cache[fullPath] && scrollBehaviorResultY !== 0) {
          itemsToRender.value.push(...cache[fullPath])
        } */
        watch(
          () => window.scrollY,
          () => {
            tryAddNextToRender()
          }
        )
      }
    )

    onBeforeUnmount(() => {
      cache[fullPath] = itemsToRender.value
    })

    return {
      rootRef,
      containerRef,
      itemsToRender,
      topHiddenItemsCount,
      bottomHiddenItemsCount,
      updateHeight,
      scrolled
    }
  }
}
</script>